import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from "amazon-cognito-identity-js";
import {
  CognitoIdentityClient,
  GetIdCommand,
  GetCredentialsForIdentityCommand,
} from "@aws-sdk/client-cognito-identity";

import { CLOUD_CONFIG } from "./config";

import type { CognitoUserSession } from "amazon-cognito-identity-js";

import type { CloudUser } from "./types";

let userPool: CognitoUserPool | null = null;

// Cognito Identity ID is stable per user for the session — fetch once.
let cachedIdentityId: string | null = null;
// STS credentials from GetCredentialsForIdentity are valid ~1h. Cache and
// reuse until shortly before expiry so we don't hit Cognito on every S3 op.
let cachedCreds: {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string | undefined;
  expiration: number; // epoch ms
} | null = null;
const CREDS_SAFETY_MARGIN_MS = 5 * 60 * 1000;

function getUserPool(): CognitoUserPool {
  if (!userPool) {
    userPool = new CognitoUserPool({
      UserPoolId: CLOUD_CONFIG.userPoolId,
      ClientId: CLOUD_CONFIG.clientId,
    });
  }
  return userPool;
}

export type SignInResult =
  | { type: "success"; user: CloudUser }
  | {
      type: "newPasswordRequired";
      completeChallenge: (newPassword: string) => Promise<CloudUser>;
    };

export async function signIn(
  username: string,
  password: string,
): Promise<SignInResult> {
  return new Promise((resolve, reject) => {
    const pool = getUserPool();
    const cognitoUser = new CognitoUser({ Username: username, Pool: pool });
    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: async (session) => {
        try {
          const user = await buildCloudUser(username, session);
          resolve({ type: "success", user });
        } catch (err) {
          reject(err);
        }
      },
      onFailure: (err) => reject(err),
      newPasswordRequired: () => {
        resolve({
          type: "newPasswordRequired",
          completeChallenge: (newPassword: string) =>
            new Promise((res, rej) => {
              cognitoUser.completeNewPasswordChallenge(
                newPassword,
                {},
                {
                  onSuccess: async (session) => {
                    try {
                      const user = await buildCloudUser(username, session);
                      res(user);
                    } catch (err) {
                      rej(err);
                    }
                  },
                  onFailure: (err) => rej(err),
                },
              );
            }),
        });
      },
    });
  });
}

export function signOut(): void {
  const currentUser = getUserPool().getCurrentUser();
  if (currentUser) {
    currentUser.signOut();
  }
  cachedIdentityId = null;
  cachedCreds = null;
}

export async function getCurrentUser(): Promise<CloudUser | null> {
  return new Promise((resolve) => {
    const cognitoUser = getUserPool().getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }

    cognitoUser.getSession(
      async (err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          resolve(null);
          return;
        }
        try {
          const user = await buildCloudUser(cognitoUser.getUsername(), session);
          resolve(user);
        } catch {
          resolve(null);
        }
      },
    );
  });
}

async function buildCloudUser(
  username: string,
  session: CognitoUserSession,
): Promise<CloudUser> {
  const idToken = session.getIdToken().getJwtToken();
  const identityId = await getIdentityId(idToken);
  return { username, identityId };
}

async function getIdentityId(idToken: string): Promise<string> {
  const client = new CognitoIdentityClient({ region: CLOUD_CONFIG.region });
  const logins = {
    [`cognito-idp.${CLOUD_CONFIG.region}.amazonaws.com/${CLOUD_CONFIG.userPoolId}`]:
      idToken,
  };

  const idResponse = await client.send(
    new GetIdCommand({
      AccountId: undefined,
      IdentityPoolId: CLOUD_CONFIG.identityPoolId,
      Logins: logins,
    }),
  );

  return idResponse.IdentityId!;
}

export async function getAWSCredentials(): Promise<{
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string | undefined;
}> {
  // Fast path: reuse cached STS creds until shortly before they expire. Avoids
  // hitting Cognito (GetId + GetCredentialsForIdentity) on every S3 operation.
  if (
    cachedCreds &&
    cachedCreds.expiration - Date.now() > CREDS_SAFETY_MARGIN_MS
  ) {
    return {
      accessKeyId: cachedCreds.accessKeyId,
      secretAccessKey: cachedCreds.secretAccessKey,
      sessionToken: cachedCreds.sessionToken,
    };
  }

  const cognitoUser = getUserPool().getCurrentUser();
  if (!cognitoUser) {
    throw new Error("Not authenticated");
  }

  return new Promise((resolve, reject) => {
    cognitoUser.getSession(
      async (err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          reject(new Error("Session invalid"));
          return;
        }

        try {
          const idToken = session.getIdToken().getJwtToken();
          const logins = {
            [`cognito-idp.${CLOUD_CONFIG.region}.amazonaws.com/${CLOUD_CONFIG.userPoolId}`]:
              idToken,
          };

          const client = new CognitoIdentityClient({
            region: CLOUD_CONFIG.region,
          });

          // Identity ID is stable per user — resolve once, then reuse.
          if (!cachedIdentityId) {
            const idResponse = await client.send(
              new GetIdCommand({
                IdentityPoolId: CLOUD_CONFIG.identityPoolId,
                Logins: logins,
              }),
            );
            cachedIdentityId = idResponse.IdentityId!;
          }

          const credsResponse = await client.send(
            new GetCredentialsForIdentityCommand({
              IdentityId: cachedIdentityId,
              Logins: logins,
            }),
          );

          const creds = credsResponse.Credentials!;
          cachedCreds = {
            accessKeyId: creds.AccessKeyId!,
            secretAccessKey: creds.SecretKey!,
            sessionToken: creds.SessionToken,
            // Expiration is a Date; fall back to ~55 min if absent.
            expiration: creds.Expiration
              ? new Date(creds.Expiration).getTime()
              : Date.now() + 55 * 60 * 1000,
          };
          resolve({
            accessKeyId: cachedCreds.accessKeyId,
            secretAccessKey: cachedCreds.secretAccessKey,
            sessionToken: cachedCreds.sessionToken,
          });
        } catch (e) {
          reject(e);
        }
      },
    );
  });
}
